import { useLiveStream, type LiveStatus } from '../hooks/useLiveStream';
import './LiveIndicator.css';

type Mode = 'live' | 'reconnecting' | 'polling';

const MODE: Record<LiveStatus, Mode> = {
  live: 'live',
  reconnecting: 'reconnecting',
  connecting: 'polling',
  offline: 'polling',
};

const LABEL: Record<Mode, string> = {
  live: 'LIVE',
  reconnecting: 'RECONNECTING',
  polling: 'POLLING',
};

/** Plain-language tooltip — what the chip means for the numbers on screen. */
const HINT: Record<Mode, string> = {
  live: 'Push connection open — panels refresh the moment a source delivers new data.',
  reconnecting: 'Push connection dropped — retrying. Panels keep refreshing on their normal timers meanwhile.',
  polling: 'Polling fallback — the live push connection is unavailable, so panels refresh on their timers (every 15 s to 30 min, per source). The data is still real, just checked less often.',
};

interface LiveIndicatorProps {
  /** Optional extra class for placement by the parent layout. */
  className?: string;
}

/**
 * Small status chip for the /api/stream connection. Mount once in the top
 * bar; it shares the single per-tab EventSource that useFeed already uses.
 */
export function LiveIndicator({ className }: LiveIndicatorProps) {
  const { status } = useLiveStream();
  const mode = MODE[status];
  return (
    <span
      className={`live-ind live-ind--${mode}${className ? ` ${className}` : ''}`}
      role="status"
      aria-live="polite"
      title={HINT[mode]}
      data-status={status}
    >
      <span className="live-ind__dot" aria-hidden="true" />
      <span className="live-ind__label">{LABEL[mode]}</span>
      <span className="live-ind__sr">{` — ${HINT[mode]}`}</span>
    </span>
  );
}
